import { createTheme } from "@mui/material/styles";
import Grow from "@mui/material/Grow";

// LX Brand Color Palette
const brandColors = {
  navy: {
    main: "#002843",
    light: "#0a4d6e",      // Brighter for better contrast
    lighter: "#1a6a8f",    // Even lighter for hover states
    dark: "#021e2d",       // Page background
    darker: "#011520",     // Deepest background
    paper: "#032d42",      // Visible card/paper background
  },
  green: {
    main: "#A0C46D",
    light: "#b8d38f",
    lighter: "#c8e0a5",    // Lighter for better visibility
    dark: "#7a9c4a",
    darker: "#5a7a35",
  },
};

// Light Mode Colors
const lightColors = {
    primary: {
    main: brandColors.navy.main,
    light: brandColors.navy.light,
    dark: brandColors.navy.dark,
    contrastText: "#ffffff",
  },
  success: {
    main: brandColors.green.main,
    light: brandColors.green.light,
    dark: brandColors.green.dark,
    contrastText: "#ffffff",
  },
    secondary: {
    main: "#64748b",
    light: "#94a3b8",
    dark: "#475569",
    contrastText: "#ffffff",
  },
    error: {
    main: "#ef4444",
    light: "#f87171",
    dark: "#dc2626",
    contrastText: "#ffffff",
    },
    warning: {
    main: "#f59e0b",
    light: "#fbbf24",
    dark: "#d97706",
    contrastText: "#ffffff",
    },
    info: {
    main: "#3b82f6",
    light: "#60a5fa",
    dark: "#2563eb",
    contrastText: "#ffffff",
  },
  background: {
    default: "#f8fafc",
    paper: "#ffffff",
  },
  text: {
    primary: "#1e293b",
    secondary: "#64748b",
    disabled: "#94a3b8",
  },
  divider: "#e2e8f0",
};

// Dark Mode Colors - Using company logo colors
const darkColors = {
  primary: {
    main: brandColors.green.main,
    light: brandColors.green.light,
    dark: brandColors.green.dark,
    contrastText: "#ffffff",
    },
    success: {
    main: brandColors.green.main,
    light: brandColors.green.light,
    dark: brandColors.green.dark,
    contrastText: "#ffffff",
  },
  secondary: {
    main: "#a3b8d1",        // Lighter for better contrast
    light: "#c4d4e8",       // Much lighter
    dark: "#7a94b3",
    contrastText: "#021e2d",
  },
  error: {
    main: "#ff7b7b",        // Brighter red
    light: "#ffa5a5",
    dark: "#ef5555",
    contrastText: "#ffffff",
  },
  warning: {
    main: "#ffc940",        // Brighter yellow
    light: "#ffd966",
    dark: "#f5a623",
    contrastText: "#021e2d",
  },
  info: {
    main: "#6db3f2",        // Brighter blue
    light: "#9dd0ff",
    dark: "#4a9de8",
    contrastText: "#ffffff",
  },
  background: {
    default: brandColors.navy.dark,    // Visible page background
    paper: brandColors.navy.paper,     // Visible card/paper background
  },
  text: {
    primary: "#ffffff",             // Pure white for best readability
    secondary: "#b8c9da",           // Lighter secondary text
    disabled: "#7a8fa3",
  },
  divider: "rgba(168, 190, 210, 0.25)",  // Lighter divider
};

// Shared component base styles
const getComponentOverrides = (mode, colors) => {
  const isDark = mode === "dark";
  const navyColor = brandColors.navy.main;
  const greenColor = brandColors.green.main;

  return {
    // Paper
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
          backgroundImage: "none",
          transition: "background-color 0.3s ease, box-shadow 0.3s ease",
          borderRadius: 12,
          // Subtle default depth for elevation={0} cards (elevation1/2/3 override)
          boxShadow: isDark
            ? "0 1px 3px rgba(0, 0, 0, 0.2)"
            : "0 1px 3px rgba(0, 0, 0, 0.06)",
        },
        rounded: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: isDark
            ? "0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 1px 2px -1px rgba(0, 0, 0, 0.3)"
            : "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)",
        },
        elevation2: {
          boxShadow: isDark
            ? "0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -2px rgba(0, 0, 0, 0.4)"
            : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
        },
        elevation3: {
          boxShadow: isDark
            ? "0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -4px rgba(0, 0, 0, 0.5)"
            : "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
        },
      },
    },

    // Button
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: "10px 22px",
          fontSize: "0.875rem",
          fontWeight: 600,
          textTransform: "none",
          boxShadow: "none",
          position: "relative",
          overflow: "hidden",
          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: "-100%",
            width: "100%",
            height: "100%",
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
            transition: "left 0.5s ease-in-out",
          },
          "&:hover::before": {
            left: "100%",
          },
          "&:hover": {
            boxShadow: isDark
              ? "0 6px 20px rgba(160, 196, 109, 0.25)"
              : "0 6px 20px rgba(0, 0, 0, 0.15)",
            transform: "translateY(-2px)",
          },
          "&:active": {
            transform: "translateY(0) scale(0.98)",
          },
        },
        containedPrimary: {
          backgroundColor: isDark ? greenColor : navyColor,
          backgroundImage: isDark
            ? `linear-gradient(180deg, ${brandColors.green.light} 0%, ${greenColor} 100%)`
            : `linear-gradient(180deg, ${brandColors.navy.light} 0%, ${navyColor} 100%)`,
          "&:hover": {
            backgroundColor: isDark ? brandColors.green.dark : brandColors.navy.light,
            boxShadow: isDark
              ? `0 8px 25px rgba(160, 196, 109, 0.4)`
              : `0 8px 25px rgba(0, 40, 67, 0.35)`,
          },
        },
        containedSecondary: {
          backgroundColor: greenColor,
          backgroundImage: `linear-gradient(180deg, ${brandColors.green.light} 0%, ${greenColor} 100%)`,
          color: "#ffffff",
          "&:hover": {
            backgroundColor: brandColors.green.dark,
            boxShadow: `0 8px 25px rgba(160, 196, 109, 0.4)`,
          },
        },
        outlinedPrimary: {
          borderColor: isDark ? greenColor : navyColor,
          borderWidth: 2,
          color: isDark ? greenColor : navyColor,
          "&:hover": {
            backgroundColor: isDark
              ? "rgba(160, 196, 109, 0.1)"
              : "rgba(0, 40, 67, 0.06)",
            borderWidth: 2,
          },
        },
        text: {
          borderRadius: 8,
          "&:hover": {
            backgroundColor: isDark
              ? "rgba(160, 196, 109, 0.15)"
              : "rgba(160, 196, 109, 0.1)",
          },
        },
      },
    },

    // IconButton
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: "all 0.2s ease-in-out",
          color: colors.text.secondary,
        "&:hover": {
            backgroundColor: isDark
              ? "rgba(160, 196, 109, 0.15)"
              : "rgba(160, 196, 109, 0.1)",
            color: greenColor,
          },
        },
      },
    },

    // TextField
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.03)" : "transparent",
            transition: "all 0.2s ease-in-out",
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: greenColor,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: greenColor,
              borderWidth: 2,
            },
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: greenColor,
          },
          "& .MuiInputBase-input": {
            color: colors.text.primary,
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: greenColor,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: greenColor,
            borderWidth: 2,
          },
        },
        notchedOutline: {
          borderColor: isDark ? "rgba(148, 163, 184, 0.3)" : "#e2e8f0",
          transition: "border-color 0.2s ease-in-out",
        },
        input: {
          color: colors.text.primary,
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: colors.text.secondary,
          "&.Mui-focused": {
            color: greenColor,
          },
        },
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: colors.text.secondary,
          "&.Mui-focused": {
            color: greenColor,
          },
        },
      },
    },

    // Card
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: isDark
            ? "0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 1px 2px -1px rgba(0, 0, 0, 0.3)"
            : "0 1px 3px 0 rgba(0, 0, 0, 0.08), 0 1px 2px -1px rgba(0, 0, 0, 0.06)",
          border: isDark
            ? "1px solid rgba(148, 163, 184, 0.15)"
            : "1px solid rgba(226, 232, 240, 0.8)",
          backgroundColor: colors.background.paper,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: `linear-gradient(90deg, ${navyColor} 0%, ${greenColor} 100%)`,
            opacity: 0,
            transition: "opacity 0.3s ease-in-out",
          },
          "&:hover": {
            boxShadow: isDark
              ? "0 20px 40px -12px rgba(0, 0, 0, 0.4)"
              : "0 20px 40px -12px rgba(0, 0, 0, 0.12)",
            transform: "translateY(-4px)",
            borderColor: isDark
              ? "rgba(160, 196, 109, 0.4)"
              : "rgba(160, 196, 109, 0.3)",
            "&::before": {
              opacity: 1,
            },
          },
        },
      },
    },

    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: "20px 24px 16px",
          borderBottom: `1px solid ${colors.divider}`,
        },
        title: {
          fontSize: "1.125rem",
          fontWeight: 700,
          color: colors.text.primary,
          letterSpacing: "-0.01em",
        },
        subheader: {
          fontSize: "0.875rem",
          color: colors.text.secondary,
          marginTop: "4px",
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: "20px 24px",
          "&:last-child": {
            paddingBottom: 24,
          },
        },
      },
    },

    // Chip
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          fontWeight: 500,
          fontSize: "0.8125rem",
        },
        colorPrimary: {
          backgroundColor: isDark ? greenColor : navyColor,
          color: "#ffffff",
        },
        colorSuccess: {
          backgroundColor: isDark
            ? "rgba(160, 196, 109, 0.2)"
            : "rgba(160, 196, 109, 0.15)",
          color: isDark ? brandColors.green.light : brandColors.green.dark,
        },
      },
    },

    // Tabs
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 52,
        },
        indicator: {
          backgroundColor: greenColor,
          height: 3,
          borderRadius: "3px 3px 0 0",
          boxShadow: `0 -2px 10px rgba(160, 196, 109, 0.4)`,
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.9rem",
          minHeight: 52,
          padding: "14px 24px",
          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          color: colors.text.secondary,
          "&:hover": {
            color: isDark ? greenColor : navyColor,
            backgroundColor: isDark
              ? "rgba(160, 196, 109, 0.1)"
              : "rgba(160, 196, 109, 0.08)",
          },
          "&.Mui-selected": {
            color: isDark ? greenColor : navyColor,
            fontWeight: 600,
          },
        },
      },
    },

    // Table
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${colors.divider}`,
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: isDark ? brandColors.navy.main : navyColor,
          "& .MuiTableCell-head": {
            color: "#ffffff",
            fontWeight: 600,
            fontSize: "0.875rem",
            borderBottom: "none",
          },
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: "background-color 0.2s ease-in-out",
          "&:hover": {
            backgroundColor: isDark
              ? "rgba(160, 196, 109, 0.08)"
              : "rgba(160, 196, 109, 0.08)",
          },
          "&:nth-of-type(even)": {
            backgroundColor: isDark
              ? "rgba(255, 255, 255, 0.02)"
              : "#f8fafc",
          },
        },
        head: {
          "&:hover": {
            backgroundColor: "transparent",
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: '"Lato", sans-serif',
          fontWeight: 400,
          fontSize: "0.875rem",
          padding: "16px",
          borderBottom: `1px solid ${colors.divider}`,
          color: colors.text.primary,
        },
        head: {
          fontWeight: 600,
          backgroundColor: isDark ? brandColors.navy.main : navyColor,
          color: "#ffffff",
        },
      },
    },

    // AppBar
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: isDark
            ? "0 1px 3px 0 rgba(0, 0, 0, 0.4)"
            : "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
        },
        colorPrimary: {
          backgroundColor: isDark ? brandColors.navy.dark : navyColor,
        },
      },
    },

    // Drawer
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: isDark ? brandColors.navy.darker : navyColor,
          borderRight: isDark ? "1px solid rgba(148, 163, 184, 0.1)" : "none",
        },
      },
    },

    // Menu
    MuiMenu: {
      defaultProps: {
        TransitionComponent: Grow,
        TransitionProps: { timeout: { enter: 200, exit: 150 } },
      },
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: isDark
            ? "0 10px 15px -3px rgba(0, 0, 0, 0.4)"
            : "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
          border: `1px solid ${colors.divider}`,
          backgroundColor: colors.background.paper,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: "10px 16px",
          fontSize: "0.875rem",
          transition: "all 0.15s ease-in-out",
          color: colors.text.primary,
          "&:hover": {
            backgroundColor: isDark
              ? "rgba(160, 196, 109, 0.15)"
              : "rgba(160, 196, 109, 0.1)",
          },
          "&.Mui-selected": {
            backgroundColor: isDark
              ? "rgba(160, 196, 109, 0.2)"
              : "rgba(160, 196, 109, 0.15)",
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(160, 196, 109, 0.25)"
                : "rgba(160, 196, 109, 0.2)",
            },
          },
        },
      },
    },

    // Dialog
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: isDark
            ? "0 25px 60px -12px rgba(0, 0, 0, 0.5)"
            : "0 25px 60px -12px rgba(0, 0, 0, 0.3)",
          backgroundColor: colors.background.paper,
          border: isDark ? `1px solid ${colors.divider}` : "none",
        },
        root: {
          "& .MuiBackdrop-root": {
            backgroundColor: isDark
              ? "rgba(0, 0, 0, 0.7)"
              : "rgba(30, 41, 59, 0.6)",
            backdropFilter: "blur(4px)",
          },
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: "1.25rem",
          fontWeight: 700,
          padding: "24px 28px 16px",
          color: colors.text.primary,
          borderBottom: `1px solid ${colors.divider}`,
          background: isDark
            ? `linear-gradient(180deg, ${colors.background.paper} 0%, ${brandColors.navy.darker} 100%)`
            : "linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)",
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "24px 28px",
          color: colors.text.primary,
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "16px 28px 24px",
          gap: 12,
          borderTop: `1px solid ${colors.divider}`,
          background: isDark
            ? `linear-gradient(180deg, ${brandColors.navy.darker} 0%, ${brandColors.navy.dark} 100%)`
            : "linear-gradient(180deg, #fafbfc 0%, #f1f5f9 100%)",
        },
      },
    },

    // Tooltip
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: isDark ? brandColors.green.dark : navyColor,
          color: "#ffffff",
          fontSize: "0.75rem",
          fontWeight: 500,
          padding: "8px 12px",
          borderRadius: 8,
        },
        arrow: {
          color: isDark ? brandColors.green.dark : navyColor,
        },
      },
    },

    // Alert
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: "12px 16px",
        },
        standardSuccess: {
          backgroundColor: isDark
            ? "rgba(160, 196, 109, 0.2)"
            : "rgba(160, 196, 109, 0.15)",
          color: isDark ? brandColors.green.light : brandColors.green.dark,
        },
        standardError: {
          backgroundColor: isDark
            ? "rgba(248, 113, 113, 0.2)"
            : "rgba(239, 68, 68, 0.1)",
          color: isDark ? "#fca5a5" : "#dc2626",
        },
        standardWarning: {
          backgroundColor: isDark
            ? "rgba(251, 191, 36, 0.2)"
            : "rgba(245, 158, 11, 0.1)",
          color: isDark ? "#fcd34d" : "#d97706",
        },
        standardInfo: {
          backgroundColor: isDark
            ? "rgba(96, 165, 250, 0.2)"
            : "rgba(59, 130, 246, 0.1)",
          color: isDark ? "#93c5fd" : "#2563eb",
        },
      },
    },

    // Divider
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.divider,
        },
      },
    },

    // Link
    MuiLink: {
      styleOverrides: {
        root: {
          color: greenColor,
          textDecorationColor: "rgba(160, 196, 109, 0.4)",
          "&:hover": {
            color: brandColors.green.dark,
          },
        },
      },
    },

    // Checkbox
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: colors.text.secondary,
          "&.Mui-checked": {
            color: greenColor,
          },
        },
      },
    },

    // Radio
    MuiRadio: {
      styleOverrides: {
        root: {
          color: colors.text.secondary,
          "&.Mui-checked": {
            color: greenColor,
          },
        },
      },
    },

    // Switch
    MuiSwitch: {
      styleOverrides: {
        root: {
          "& .Mui-checked": {
            color: greenColor,
            "& + .MuiSwitch-track": {
              backgroundColor: greenColor,
            },
          },
        },
        track: {
          backgroundColor: colors.text.disabled,
        },
      },
    },

    // LinearProgress
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: isDark
            ? "rgba(160, 196, 109, 0.3)"
            : "rgba(160, 196, 109, 0.2)",
        },
        bar: {
          borderRadius: 4,
          backgroundColor: greenColor,
        },
      },
    },

    // CircularProgress
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: greenColor,
        },
      },
    },

    // Autocomplete
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: isDark
            ? "0 10px 15px -3px rgba(0, 0, 0, 0.4)"
            : "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
          border: `1px solid ${colors.divider}`,
          backgroundColor: colors.background.paper,
        },
        option: {
          padding: "10px 16px",
          color: colors.text.primary,
          "&:hover": {
            backgroundColor: isDark
              ? "rgba(160, 196, 109, 0.15)"
              : "rgba(160, 196, 109, 0.1)",
          },
          "&[aria-selected='true']": {
            backgroundColor: isDark
              ? "rgba(160, 196, 109, 0.2)"
              : "rgba(160, 196, 109, 0.15)",
          },
        },
      },
    },

    // Pagination
    MuiPagination: {
      styleOverrides: {
        root: {
          "& .MuiPaginationItem-root": {
            borderRadius: 8,
            color: colors.text.primary,
            "&.Mui-selected": {
              backgroundColor: greenColor,
              color: "#ffffff",
              "&:hover": {
                backgroundColor: brandColors.green.dark,
              },
            },
          },
        },
      },
    },

    // List
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            backgroundColor: isDark
              ? "rgba(160, 196, 109, 0.15)"
              : "rgba(160, 196, 109, 0.1)",
          },
          "&.Mui-selected": {
            backgroundColor: isDark
              ? "rgba(160, 196, 109, 0.2)"
              : "rgba(160, 196, 109, 0.15)",
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(160, 196, 109, 0.25)"
                : "rgba(160, 196, 109, 0.2)",
            },
          },
        },
      },
    },

    // Avatar
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: "0.875rem",
        },
        colorDefault: {
          backgroundColor: greenColor,
          color: "#ffffff",
        },
      },
    },

    // Select
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          color: colors.text.primary,
        },
        select: {
          color: colors.text.primary,
          backgroundColor: colors.background.paper,
          "&:focus": {
            backgroundColor: colors.background.paper,
          },
        },
        icon: {
          color: colors.text.secondary,
        },
      },
    },

    // NativeSelect
    MuiNativeSelect: {
      styleOverrides: {
        root: {
          color: colors.text.primary,
        },
        select: {
          color: colors.text.primary,
          backgroundColor: colors.background.paper,
          "&:focus": {
            backgroundColor: colors.background.paper,
          },
          "& option": {
            backgroundColor: colors.background.paper,
            color: colors.text.primary,
          },
        },
        icon: {
          color: colors.text.secondary,
        },
      },
    },

    // InputBase for native selects
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: colors.text.primary,
          "& .MuiNativeSelect-select": {
            color: colors.text.primary,
          },
        },
        input: {
          color: colors.text.primary,
          "&::placeholder": {
            color: colors.text.secondary,
            opacity: 1,
          },
        },
      },
    },

    // Popover (used by Select dropdowns)
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.background.paper,
          color: colors.text.primary,
        },
      },
    },

    // Skeleton
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: isDark
            ? "rgba(148, 163, 184, 0.15)"
            : "rgba(148, 163, 184, 0.2)",
        },
      },
    },

    // CssBaseline for global styles
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.background.default,
          color: colors.text.primary,
          transition: "background-color 0.3s ease, color 0.3s ease",
          scrollbarColor: isDark
            ? `${brandColors.green.dark} ${brandColors.navy.darker}`
            : `#94a3b8 #f1f5f9`,
          "&::-webkit-scrollbar": {
            width: "10px",
            height: "10px",
          },
          "&::-webkit-scrollbar-track": {
            background: isDark ? brandColors.navy.darker : "#f1f5f9",
          },
          "&::-webkit-scrollbar-thumb": {
            background: isDark ? brandColors.green.dark : "#94a3b8",
            borderRadius: "5px",
            "&:hover": {
              background: isDark ? greenColor : "#64748b",
            },
          },
        },
      },
    },
  };
};

// Typography configuration
const typography = {
  fontFamily: '"Lato", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: {
    fontSize: "2.5rem",
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: "-0.02em",
  },
  h2: {
    fontSize: "2rem",
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: "-0.01em",
  },
  h3: {
    fontSize: "1.5rem",
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h4: {
    fontSize: "1.25rem",
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h5: {
    fontSize: "1.125rem",
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h6: {
    fontSize: "1rem",
    fontWeight: 600,
    lineHeight: 1.5,
  },
  body1: {
    fontSize: "1rem",
    fontWeight: 400,
    lineHeight: 1.6,
  },
  body2: {
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.6,
  },
  subtitle1: {
    fontSize: "1rem",
    fontWeight: 500,
    lineHeight: 1.5,
  },
  subtitle2: {
    fontSize: "0.875rem",
    fontWeight: 500,
    lineHeight: 1.5,
  },
  button: {
    textTransform: "none",
    fontWeight: 600,
    letterSpacing: "0.02em",
  },
  caption: {
    fontSize: "0.75rem",
    fontWeight: 400,
    lineHeight: 1.5,
  },
  overline: {
    fontSize: "0.75rem",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
};

// Create Light Theme
export const createLightTheme = () =>
  createTheme({
    palette: {
      mode: "light",
      ...lightColors,
      third: { main: "#414348" },
      action: {
        main: "#3b82f6",
        hover: "rgba(160, 196, 109, 0.08)",
        selected: "rgba(160, 196, 109, 0.12)",
        focus: "rgba(160, 196, 109, 0.12)",
      },
      typography: {
        primary: lightColors.text.primary,
        secondary: lightColors.text.secondary,
        light: "#94a3b8",
      },
    },
    typography: {
      ...typography,
      h1: { ...typography.h1, color: lightColors.text.primary },
      h2: { ...typography.h2, color: lightColors.text.primary },
      h3: { ...typography.h3, color: lightColors.text.primary },
      h4: { ...typography.h4, color: lightColors.text.primary },
      h5: { ...typography.h5, color: lightColors.text.primary },
      h6: { ...typography.h6, color: lightColors.text.primary },
      body1: { ...typography.body1, color: lightColors.text.primary },
      body2: { ...typography.body2, color: lightColors.text.secondary },
      subtitle1: { ...typography.subtitle1, color: lightColors.text.primary },
      subtitle2: { ...typography.subtitle2, color: lightColors.text.secondary },
      caption: { ...typography.caption, color: lightColors.text.secondary },
      overline: { ...typography.overline, color: lightColors.text.secondary },
    },
    shape: { borderRadius: 8 },
    components: getComponentOverrides("light", lightColors),
  });

// Create Dark Theme
export const createDarkTheme = () =>
  createTheme({
    palette: {
      mode: "dark",
      ...darkColors,
      third: { main: "#8b9dc3" },
      action: {
        main: "#60a5fa",
        hover: "rgba(160, 196, 109, 0.12)",
        selected: "rgba(160, 196, 109, 0.16)",
        focus: "rgba(160, 196, 109, 0.16)",
      },
      typography: {
        primary: darkColors.text.primary,
        secondary: darkColors.text.secondary,
        light: "#64748b",
      },
    },
    typography: {
      ...typography,
      h1: { ...typography.h1, color: darkColors.text.primary },
      h2: { ...typography.h2, color: darkColors.text.primary },
      h3: { ...typography.h3, color: darkColors.text.primary },
      h4: { ...typography.h4, color: darkColors.text.primary },
      h5: { ...typography.h5, color: darkColors.text.primary },
      h6: { ...typography.h6, color: darkColors.text.primary },
      body1: { ...typography.body1, color: darkColors.text.primary },
      body2: { ...typography.body2, color: darkColors.text.secondary },
      subtitle1: { ...typography.subtitle1, color: darkColors.text.primary },
      subtitle2: { ...typography.subtitle2, color: darkColors.text.secondary },
      caption: { ...typography.caption, color: darkColors.text.secondary },
      overline: { ...typography.overline, color: darkColors.text.secondary },
    },
    shape: { borderRadius: 8 },
    components: getComponentOverrides("dark", darkColors),
  });

// Default export for backward compatibility
export const theme = createLightTheme();
